import React, { useEffect, useState } from 'react';
import api from '@/api/engine';
import type { CreatorProfile } from '@/api/types';
import { SettingsSection } from '@seitiate/settings-ui';

interface AlterEgoSettings {
    persona_name: string | null;
    interaction_style: string;
    autonomy_override: string | null;
}

function percentOrDash(value: number | null | undefined): string {
    if (typeof value !== 'number' || Number.isNaN(value)) return 'Not ready';
    return `${Math.round(value * 100)}%`;
}

export const TrustBondPanel: React.FC = () => {
    const [profile, setProfile] = useState<CreatorProfile | null>(null);
    const [seitySettings, setSeitySettings] = useState<AlterEgoSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const isUnavailable = (reason: unknown): boolean =>
        reason instanceof Error &&
        (reason.message.toLowerCase().includes('404') || reason.message.toLowerCase().includes('not available'));

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const [profileResult, seityResult] = await Promise.allSettled([
                    api.creator.getProfile(),
                    api.settings.getCategory('alter_ego'),
                ]);

                if (profileResult.status === 'fulfilled') {
                    setProfile(profileResult.value);
                } else if (!isUnavailable(profileResult.reason)) {
                    setError(profileResult.reason instanceof Error ? profileResult.reason.message : 'Failed to load trust signals');
                }

                if (seityResult.status === 'fulfilled') {
                    setSeitySettings(seityResult.value.settings as unknown as AlterEgoSettings);
                }
            } finally {
                setLoading(false);
            }
        };

        void load();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500 font-mono text-sm animate-pulse">Loading bond posture...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-red-400 font-mono text-sm">{error}</div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-gray-300">
                Bond & Trust :: Summary
            </h3>

            <SettingsSection title="Current Posture">
                <div className="grid gap-3 md:grid-cols-3">
                    <Metric label="Assistant" value={seitySettings?.persona_name || 'your assistant'} />
                    <Metric label="Interaction style" value={seitySettings?.interaction_style || 'Balanced'} />
                    <Metric label="Autonomy override" value={seitySettings?.autonomy_override || 'Computed'} />
                </div>
                <p className="text-[10px] text-gray-500">
                    This page now shows the live identity posture Sakme already stores. Formal bond documents and trust proofs
                    still live deeper in Seitiate and ClosedClaw.
                </p>
            </SettingsSection>

            <SettingsSection title="Read-Only Trust Signals" badge={profile ? 'LIVE' : 'NOT READY'}>
                {profile ? (
                    <div className="grid gap-3 md:grid-cols-2">
                        <Metric label="Voice calibrated" value={profile.voice_calibrated ? 'Yes' : 'Not yet'} />
                        <Metric label="Average blessing" value={percentOrDash(profile.average_blessing_score)} />
                        <Metric label="Governor engagement" value={percentOrDash(profile.governor_override_rate)} />
                        <Metric label="Original content ratio" value={percentOrDash(profile.original_content_ratio)} />
                    </div>
                ) : (
                    <p className="text-[10px] text-gray-500">
                        Trust signals will appear once creator profile computation has enough material to analyze.
                    </p>
                )}
            </SettingsSection>

            <SettingsSection title="Identity Statement" defaultExpanded={Boolean(profile?.identity_statement)}>
                {profile?.identity_statement ? (
                    <p className="text-sm text-gray-300 leading-6">{profile.identity_statement}</p>
                ) : (
                    <p className="text-[10px] text-gray-500">
                        No identity statement is available yet. The backend currently exposes it only after profile maturity.
                    </p>
                )}
            </SettingsSection>

            <SettingsSection title="Bond credentials (boundary)" defaultExpanded={false} badge="CREATOR VIEW">
                <p className="text-[10px] text-gray-500 leading-5">
                    Formal bond documents, ceremony state, and certification internals stay in Seitiate / ClosedClaw operator
                    surfaces. This summary is intentionally read-first: it shows posture and trust signals, not signing keys or
                    audit payloads.
                </p>
            </SettingsSection>
        </div>
    );
};

const Metric: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="rounded border border-gray-800 bg-gray-900/40 p-3">
        <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">{label}</div>
        <div className="mt-2 text-sm font-semibold text-gray-200">{value}</div>
    </div>
);
